import { memo } from "react";
import { Skeleton } from "@/core/components/ui/skeleton";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { useGetActivityReferral } from "@/activity-feed/query";
import { formatPhoneNumber } from "@/core/utils/phone";
import { getCustomerNameOrPhone } from "@/customer/utils";
import { SPIFF_RULE_EVENTS } from "@/spiff-rules/constants";
import { DetailCard, InfoPair } from "./common";

interface NewReferralDetailCardProps {
  referralId: string;
  inView: boolean;
}

export const NewReferralDetailCard = memo(
  ({ referralId, inView }: NewReferralDetailCardProps) => {
    const { data: referral, isLoading } = useGetActivityReferral(
      referralId,
      inView,
    );

    // Show loading only if we don't have data yet
    if (!referral && (!inView || isLoading)) {
      return (
        <DetailCard eventType={SPIFF_RULE_EVENTS.referral_creation}>
          <Stack gap="sm" fullWidth>
            <Skeleton className="h-5 w-48" />
            <div className="flex flex-wrap gap-x-10 gap-y-2">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-48" />
            </div>
          </Stack>
        </DetailCard>
      );
    }

    if (!referral) {
      return (
        <DetailCard eventType={SPIFF_RULE_EVENTS.referral_creation}>
          <Stack gap="sm" fullWidth>
            <Typography variant="subtitleW2">Referral Details</Typography>
            <Typography variant="bodyW3" textColor="secondary">
              Referral information unavailable
            </Typography>
          </Stack>
        </DetailCard>
      );
    }

    return (
      <DetailCard eventType={SPIFF_RULE_EVENTS.referral_creation}>
        <Stack gap="sm" fullWidth>
          <Typography variant="subtitleW2">
            {getCustomerNameOrPhone(referral.potential_customer)}
          </Typography>
          <div className="flex flex-wrap gap-x-10 gap-y-2">
            <InfoPair
              label="Phone"
              value={formatPhoneNumber(
                referral.potential_customer.phone_number,
              )}
            />
            <InfoPair label="Service" value={referral.service_needed} />
            <InfoPair
              label="Advocate"
              value={getCustomerNameOrPhone(referral.advocate)}
            />
            <InfoPair
              label="Advocate Phone"
              value={formatPhoneNumber(referral.advocate.phone_number)}
            />
          </div>
        </Stack>
      </DetailCard>
    );
  },
);

NewReferralDetailCard.displayName = "NewReferralDetailCard";
